import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  AuditAlertDigestContext,
  AuditAlertImmediateContext
} from "../../auditLog.types";
import { buildAlertSummary, isDigestContext } from "../../helpers";
import { InfoBanner } from "../InfoBanner";
import { JsonDiff } from "../JsonDiff";
import { SeverityBadge } from "../SeverityBadge";
import { AuditAlertDetailContentProps } from "./AuditAlertDetailContent.types";

import styles from "./AuditAlertDetailContent.module.scss";

const RELATED_IDS_PREVIEW_LIMIT = 20;

const AuditAlertDetailContent = ({
  auditAlert
}: AuditAlertDetailContentProps) => {
  const { t } = useTranslation();
  const [showAllRelated, setShowAllRelated] = useState(false);

  const {
    severity,
    context,
    relatedAuditLogIds,
    isAcknowledged,
    acknowledgedAt,
    acknowledgedByName,
    createdAt
  } = auditAlert;

  const digest = isDigestContext(context);

  const visibleRelatedIds = showAllRelated
    ? relatedAuditLogIds
    : relatedAuditLogIds.slice(0, RELATED_IDS_PREVIEW_LIMIT);

  return (
    <div className={styles.alert}>
      <div className={styles.alert__top}>
        <SeverityBadge severity={severity} />
        <span className={styles.alert__kind}>
          {digest ? t("Digest") : t("Immediate")}
        </span>
        <span className={styles.alert__time}>
          {moment(createdAt).format("DD/MM/YYYY HH:mm")}
        </span>
      </div>

      <h3 className={styles.alert__summary}>{buildAlertSummary(auditAlert)}</h3>

      {isAcknowledged && (
        <InfoBanner>
          {t("Reviewed by")} <b>{acknowledgedByName || t("an admin")}</b>
          {acknowledgedAt
            ? ` ${t("at")} ${moment(acknowledgedAt).format(
                "DD/MM/YYYY HH:mm"
              )}`
            : ""}
        </InfoBanner>
      )}

      {isDigestContext(context) ? (
        <DigestBody context={context} />
      ) : (
        <ImmediateBody context={context} />
      )}

      <div className={styles.alert__related}>
        <h4 className={styles.alert__sectionTitle}>
          {t("Related activity records")} ({relatedAuditLogIds.length})
        </h4>

        {relatedAuditLogIds.length ? (
          <>
            <ul className={styles.alert__relatedList}>
              {visibleRelatedIds.map((id) => (
                <li key={id}>
                  <Link className={styles.alert__relatedLink} to={`/activity-log/${id}`}>
                    {id}
                  </Link>
                </li>
              ))}
            </ul>

            {!showAllRelated &&
              relatedAuditLogIds.length > RELATED_IDS_PREVIEW_LIMIT && (
                <button
                  type="button"
                  className={styles.alert__loadMore}
                  onClick={() => setShowAllRelated(true)}
                >
                  {t("Show all")} ({relatedAuditLogIds.length})
                </button>
              )}
          </>
        ) : (
          <p className={styles.alert__empty}>{t("No related records")}</p>
        )}
      </div>
    </div>
  );
};

const DigestBody = ({ context }: { context: AuditAlertDigestContext }) => {
  const { t } = useTranslation();

  const rows = Object.entries(context.byAction ?? {}).sort(
    ([, a], [, b]) => b - a
  );

  const windowFrom = context.windowStart;
  const windowTo = context.windowEnd;

  return (
    <div className={styles.digest}>
      <div className={styles.digest__meta}>
        <div>
          <span className={styles.digest__label}>{t("Window")}</span>
          <span>
            {moment(windowFrom).format("DD/MM/YYYY HH:mm")} —{" "}
            {moment(windowTo).format("DD/MM/YYYY HH:mm")}
          </span>
        </div>
        <div>
          <span className={styles.digest__label}>{t("Total actions")}</span>
          <span>{context.totalActions}</span>
        </div>
      </div>

      <table className={styles.digest__table}>
        <thead>
          <tr>
            <th>{t("Action")}</th>
            <th>{t("Count")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([action, count]) => (
            <tr key={action}>
              <td>
                <code>{action}</code>
              </td>
              <td>{count}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <Link
        className={styles.digest__link}
        to={`/activity-log?dateRange=${encodeURIComponent(
          windowFrom
        )}&dateRange=${encodeURIComponent(windowTo)}`}
      >
        {t("Open activity log for this window")}
      </Link>
    </div>
  );
};

const ImmediateBody = ({
  context
}: {
  context: AuditAlertImmediateContext;
}) => {
  const { t } = useTranslation();

  const fields = [
    { label: t("Admin"), value: context.adminName },
    { label: t("Action"), value: context.action },
    { label: t("Entity type"), value: context.entityType },
    { label: t("Entity ID"), value: context.entityId },
    { label: t("IP address"), value: context.ip },
    {
      label: t("Occurred at"),
      value: context.occurredAt
        ? moment(context.occurredAt).format("DD/MM/YYYY HH:mm:ss")
        : undefined
    }
  ].filter((field) => field.value);

  const hasDiff =
    context.previousValue != null || context.newValue != null;

  return (
    <div className={styles.immediate}>
      {!!fields.length && (
        <div className={styles.immediate__fields}>
          {fields.map(({ label, value }) => (
            <div key={label} className={styles.immediate__field}>
              <span className={styles.immediate__label}>{label}</span>
              <span className={styles.immediate__value}>
                {String(value)}
              </span>
            </div>
          ))}
        </div>
      )}

      {hasDiff && (
        <div className={styles.immediate__changes}>
          <h4 className={styles.alert__sectionTitle}>{t("Changes")}</h4>
          <JsonDiff
            previousValue={context.previousValue}
            newValue={context.newValue}
          />
        </div>
      )}
    </div>
  );
};

export default AuditAlertDetailContent;
